import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { config } from "../../config.js";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import "./SubAdmin.css";
import { toast } from "react-toastify";
import { LuArrowLeft } from "react-icons/lu";

function SubAdmin() {
  const { isSidebar, adminPermissions } = useContext(SidebarContext);
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const role = localStorage.getItem("adminRole");
  const navigate = useNavigate();
  //  Fetch Sub Admins
  const fetchSubAdmins = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config?.backEndBaseUrl}sub-admin/read`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-dashboard-request": "true",
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result.status && Array.isArray(result.sub_admin)) {
        setSubAdmins(result.sub_admin);
      } else {
        setSubAdmins([]);
      }
    } catch (error) {
      console.error("Fetching sub_admin failed:", error);
      setSubAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  //  Toggle Button
  const ToggleButtonStatus = ({ uid, initialStatus }) => {
    const [status, setStatus] = useState(initialStatus);

    const handleToggleStatus = async () => {
      // Only allow admins or sub-admins with permission
      if (role === "admin" || adminPermissions?.sub_admin_edit) {
        const newStatus = !status;
        setStatus(newStatus); // instant UI feedback

        try {
          const response = await fetch(
            `${config.backEndBaseUrl}sub-admin/status/${uid}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
            },
          );

          if (!response.ok) throw new Error("Network response was not ok");

          // Update local state (only for that UID)
          setSubAdmins((prev) =>
            prev.map((admin) =>
              admin.uid === uid ? { ...admin, active: newStatus } : admin,
            ),
          );
        } catch (error) {
          console.error("Error updating status:", error);
          setStatus(!newStatus); // revert on error
          toast.error("Failed to update status.");
        }
      } else {
        toast.error("You don't have permission to change sub-admin status.");
      }
    };

    return (
      <div className="toggle-container" onClick={handleToggleStatus}>
        <div className={`toggle-switch ${status ? "active" : ""}`}>
          <div className="toggle-knob"></div>
        </div>
        <span className={`toggle-text ${status ? "active" : ""}`}>
          {status ? "active" : "deactive"}
        </span>
      </div>
    );
  };

  //  Columns
  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Name" },
      { accessorKey: "username", header: "Username" },
      { accessorKey: "city", header: "City" },
      { accessorKey: "email", header: "Email" },
      {
        accessorKey: "active",
        header: "Status",
        Cell: ({ cell }) => (
          <ToggleButtonStatus
            uid={cell.row.original.uid}
            initialStatus={cell.getValue()}
          />
        ),
      },
      {
        accessorKey: "uid",
        header: "Permissions",
        Cell: ({ cell }) => {
          return role === "admin" || adminPermissions?.sub_admin_edit ? (
            <Link
              to={`/admin/sub/update/permissions/${cell.row.original.uid}`}
              className="btn text-light d-flex align-items-center btn-dark m-1"
              style={{ width: "50px" }}
            >
              Update
            </Link>
          ) : (
            <span className="text-muted">No Access</span>
          );
        },
      },
      {
        accessorKey: "uid",
        header: "Update",
        Cell: ({ cell }) => {
          return role === "admin" || adminPermissions?.sub_admin_edit ? (
            <Link
              to={`/admin/sub/update/${cell.row.original.uid}`}
              className="btn text-light btn-success d-flex align-items-center"
              style={{ width: "50px" }}
            >
              Update
            </Link>
          ) : (
            <span className="text-muted">No Access</span>
          );
        },
      },
    ],
    [],
  );

  //  Skeleton Loader
  const SkeletonLoader = () => (
    <div className="skeleton-wrapper">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton-row">
          <div className="skeleton-cell short"></div>
          <div className="skeleton-cell"></div>
          <div className="skeleton-cell long"></div>
          <div className="skeleton-cell small"></div>
          <div className="skeleton-cell small"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page" className="">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : "0px" }}
              >
                <div className="main-content-inner">
                  <div className="main-content-wrap">
                    <div className="tf-section">
                      <div className="wg-box" style={{ marginBottom: "20px" }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                              <button
                                className="back-btn flex items-center gap-2"
                                onClick={() => navigate("/")}
                              >
                                <LuArrowLeft size={16} />
                                {/* <span>Back</span> */}
                              </button>

                              <h5 className="fare-heading ">
                                Sub Admin Overview
                              </h5>
                            </div>
                          </div>
                          {adminPermissions?.sub_admin_add ||
                          role === "admin" ? (
                            <Link
                              to="/admin/sub/add"
                              className="btn btn-lg btn-success d-flex align-items-center"
                            >
                              Add Sub Admin +
                            </Link>
                          ) : null}
                        </div>

                        {loading ? (
                          <SkeletonLoader />
                        ) : (
                          <ReactTable columns={columns} data={subAdmins} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubAdmin;
